/*
 * Product personalization + purchase validation (FiruBack).
 *
 * Responsibilities
 *  - Validate the pack / variant selection made in the Rapi Bundle widget and every
 *    personalization field marked as required before anything is added to the cart.
 *  - Gate the main add-to-cart button (capture-phase listener, runs before the Rapi
 *    Bundle click handler that is bound directly on the button).
 *  - Mirror the Rapi 5 s re-entrancy lock so a double click never adds a bare variant.
 *  - Keep the personalization block right below the bundle widget even if the app
 *    falls back to automatic placement.
 *  - Start every page load with empty fields (nothing is persisted).
 *  - Expose window.FiruProductSelection for the sticky add-to-cart bar and emit
 *    "firu:selection:lock" / "firu:selection:unlock" events on document.
 */
(function () {
  'use strict';

  if (window.FiruProductSelection) return;

  var RAPI_CONTENT_SELECTOR = '[class^="rapi-bundle-content"]';
  var LOCK_MS = 5000;

  var state = {
    inFlight: false,
    lockTimer: null,
    stickyDispatch: false,
    documentListenersBound: false
  };

  /* ---------- helpers ---------- */

  function getRoot() {
    return document.querySelector('[data-product-personalization]');
  }

  function getForm(root) {
    var formId = root && root.dataset.formId;
    return (formId && document.getElementById(formId)) || document.querySelector('form[data-type="add-to-cart-form"]');
  }

  function getSubmitButton(root) {
    var form = getForm(root);
    return (form && form.querySelector('.product-form__submit')) || document.querySelector('.product-form__submit');
  }

  function getFields(root) {
    return root ? Array.prototype.slice.call(root.querySelectorAll('[data-personalization-field]')) : [];
  }

  function getFieldInput(field) {
    return field ? field.querySelector('[data-personalization-input]') : null;
  }

  function isRequired(field) {
    return !!field && field.dataset.required === 'true';
  }

  function getNameInput(root) {
  return root ? root.querySelector('[data-personalization-name]') : null;
}

function getSecondNameField(root) {
  return root
    ? root.querySelector('[data-personalization-field="name-2"]')
    : null;
}

function getSecondNameInput(root) {
  return root
    ? root.querySelector('[data-personalization-name-2]')
    : null;
}

function getPackUnitCount() {
  var bar = getSelectedBar(getRapiContent());

  if (!bar) return 1;

  var selects = Array.prototype.slice.call(
    bar.querySelectorAll(
      'select.rapi-bundles__bar-variant-select'
    )
  ).filter(isVisible);

  // Cada arnés de FiruBack tiene 2 selectores:
  // 1 de talla + 1 de color.
  return Math.max(1, Math.ceil(selects.length / 2));
}

function syncNameFieldsForPack() {
  var root = getRoot();

  if (!root) return;

  var secondField = getSecondNameField(root);
  var secondInput = getSecondNameInput(root);
  var firstLabel = root.querySelector(
    '[data-personalization-name-label]'
  );

  if (!secondField) return;

  var hasSecondDog = getPackUnitCount() > 1;

  secondField.hidden = !hasSecondDog;

  var requiredWhenVisible =
    secondField.dataset.requiredWhenVisible === 'true';

  var secondRequired =
    hasSecondDog && requiredWhenVisible;

  secondField.dataset.required =
    secondRequired ? 'true' : 'false';

  if (secondInput) {
    if (secondRequired) {
      secondInput.setAttribute('required', '');
      secondInput.setAttribute('aria-required', 'true');
    } else {
      secondInput.removeAttribute('required');
      secondInput.removeAttribute('aria-required');
    }

    if (!hasSecondDog) {
      secondInput.value = '';
      setFieldError(secondField, false);
    }
  }

  if (firstLabel) {
    var baseLabel =
      firstLabel.dataset.baseLabel || 'Nombre del perro';

    firstLabel.textContent =
      hasSecondDog ? baseLabel + ' #1' : baseLabel;
  }
}

function useSecondNameForRapiClick(root) {
  var firstInput = getNameInput(root);
  var secondField = getSecondNameField(root);
  var secondInput = getSecondNameInput(root);

  if (
    !firstInput ||
    !secondField ||
    secondField.hidden ||
    !secondInput
  ) {
    return;
  }

  var originalValue = firstInput.value;
  var secondValue = secondInput.value.trim();

  if (!secondValue) return;

  firstInput.value = secondValue;

  window.setTimeout(function () {
    firstInput.value = originalValue;
  }, 0);
}
  

  function isVisible(el) {
    return !!(el && el.offsetParent !== null);
  }

  function emit(name) {
    try {
      document.dispatchEvent(new CustomEvent(name));
    } catch (e) { /* ignore */ }
  }

  function getRapiContent() {
    return document.querySelector(RAPI_CONTENT_SELECTOR);
  }

  function getSelectedBar(content) {
    if (!content) return null;
    var bar = content.querySelector('.rapi-bundles__bar.selected');
    if (!bar) {
      var radio = content.querySelector('.rapi-bundles__bar input[type="radio"]:checked, .rapi-bundles__bar-radio:checked');
      if (radio) bar = radio.closest('.rapi-bundles__bar');
    }
    return bar;
  }

  /* ---------- validation ---------- */

  function bundleProblem() {
    var content = getRapiContent();
    // No widget on the page: the theme form (hidden variant id) is authoritative.
    if (!content) return null;
    // Widget still rendering: let the app handle it.
    if (!content.querySelector('.rapi-bundles__bar')) return null;

    var bar = getSelectedBar(content);
    if (!bar) {
      return { type: 'bundle', el: content };
    }

    var selects = bar.querySelectorAll('select.rapi-bundles__bar-variant-select');
    for (var i = 0; i < selects.length; i++) {
      var select = selects[i];
      if (isVisible(select) && !String(select.value || '').trim()) {
        return { type: 'variant', el: select };
      }
    }
    return null;
  }

  function fieldProblem() {
    var fields = getFields(getRoot());
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      var input = getFieldInput(field);
      if (!input || !isRequired(field)) continue;
      if (!input.value.trim()) {
        return { type: 'field', el: input, field: field };
      }
    }
    return null;
  }

  function firstProblem() {
  syncNameFieldsForPack();
  return bundleProblem() || fieldProblem();
}
  

  function isComplete() {
    return firstProblem() === null;
  }

  function setFieldError(field, show) {
    if (!field) return;
    var error = field.querySelector('.product-personalization__error');
    var input = getFieldInput(field);
    field.classList.toggle('product-personalization__field--error', !!show);
    if (error) error.hidden = !show;
    if (input) input.setAttribute('aria-invalid', show ? 'true' : 'false');
  }

  function clearErrors() {
    getFields(getRoot()).forEach(function (field) { setFieldError(field, false); });
  }

  function scrollTo(el) {
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      el.scrollIntoView();
    }
  }

  function highlight(el) {
    if (!el) return;
    el.classList.remove('product-personalization--highlight');
    // force reflow so the animation can restart
    void el.offsetWidth;
    el.classList.add('product-personalization--highlight');
  }

  function focusLater(el) {
    window.setTimeout(function () {
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }, 350);
  }

  function focusProblem(problem) {
    problem = problem || firstProblem();
    if (!problem) return;

    if (problem.type === 'field') {
      setFieldError(problem.field, true);
      highlight(getRoot());
      scrollTo(problem.el);
      focusLater(problem.el);
      return;
    }

    scrollTo(problem.el);
    if (problem.type === 'variant') focusLater(problem.el);
  }

  /* ---------- in-flight lock (mirrors the Rapi 5 s lockout) ---------- */

  function unlock() {
    var wasLocked = state.inFlight;
    state.inFlight = false;
    if (state.lockTimer) {
      window.clearTimeout(state.lockTimer);
      state.lockTimer = null;
    }
    restorePhone();
    if (wasLocked) emit('firu:selection:unlock');
  }

  function lock() {
    state.inFlight = true;
    if (state.lockTimer) window.clearTimeout(state.lockTimer);
    state.lockTimer = window.setTimeout(unlock, LOCK_MS);
    emit('firu:selection:lock');
  }

  /* Trim values; an empty optional phone must not create an empty "Teléfono" property. */
  
function getPhoneInput(root) {
  if (!root) return null;

  var fields = getFields(root);

  for (var i = 0; i < fields.length; i++) {
    var field = fields[i];
    var input = getFieldInput(field);

    if (!input) continue;

    var label = field.querySelector('label');

    var identifier = [
      input.name || '',
      input.id || '',
      input.placeholder || '',
      label ? label.textContent : ''
    ].join(' ');

    if (/tel[eé]fono|phone/i.test(identifier)) {
      return input;
    }
  }

  return null;
}

  function prepareSubmission() {
    var root = getRoot();
    getFields(root).forEach(function (field) {
      var input = getFieldInput(field);
      if (input) input.value = input.value.trim();
    });
    var phone = getPhoneInput(root);
    if (phone && !phone.value) {
      phone.disabled = true;
      phone.dataset.autoDisabled = 'true';
    }
  }

  function restorePhone() {
    var phone = getPhoneInput(getRoot());
    if (phone && phone.dataset.autoDisabled === 'true') {
      phone.disabled = false;
      delete phone.dataset.autoDisabled;
    }
  }

  /* ---------- fresh fields on every page load ---------- */

  function resetFields(root) {
    getFields(root).forEach(function (field) {
      var input = getFieldInput(field);
      if (input) {
        input.value = '';
        input.disabled = false;
        delete input.dataset.autoDisabled;
      }
    });
    clearErrors();
  }

  /* ---------- gating ---------- */

  function isOurSubmit(target, root) {
    var button = target && target.closest ? target.closest('.product-form__submit') : null;
    if (!button) return null;
    var form = button.form || button.closest('form');
    var formId = root && root.dataset.formId;
    // form.id would return the <input name="id"> control, so read the attribute instead
    if (form && formId && form.getAttribute('id') !== formId) return null; // quick-add or other forms
    return button;
  }

  function onDocumentClick(event) {
    var root = getRoot();
    if (!root) return;
    var button = isOurSubmit(event.target, root);
    if (!button) return;

   var userInitiated = event.isTrusted || state.stickyDispatch;
state.stickyDispatch = false;

// Cuando Rapi hace su clic interno para añadir la segunda unidad,
// usamos temporalmente el nombre del perro #2.
if (!userInitiated) return;


    if (state.inFlight) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var problem = firstProblem();
    if (problem) {
      event.preventDefault();
      event.stopImmediatePropagation();
      focusProblem(problem);
      return;
    }

    clearErrors();
    prepareSubmission();
    lock();
  }

  function onFormSubmit(event) {
    var root = getRoot();
    if (!root) return;
    var form = event.target;
    if (!form || form.getAttribute('id') !== root.dataset.formId) return;
    var problem = firstProblem();
    if (problem) {
      event.preventDefault();
      event.stopImmediatePropagation();
      focusProblem(problem);
      return;
    }
    prepareSubmission();
  }

  function bindDocumentListeners() {
    if (state.documentListenersBound) return;
    state.documentListenersBound = true;
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('submit', onFormSubmit, true);
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      if (window.PUB_SUB_EVENTS.cartUpdate) window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, unlock);
      if (window.PUB_SUB_EVENTS.cartError) window.subscribe(window.PUB_SUB_EVENTS.cartError, unlock);
    }

    // Back/forward cache and browser form restoration must not bring old values back.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) resetFields(getRoot());
    });
  }

  /* ---------- init ---------- */

  function init() {
    var root = getRoot();
    if (!root) return;
    bindDocumentListeners();
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    resetFields(root);
syncNameFieldsForPack();

    getFields(root).forEach(function (field) {
      var input = getFieldInput(field);
      if (!input) return;
      input.addEventListener('input', function () {
        if (input.value.trim()) setFieldError(field, false);
      });
      input.addEventListener('keydown', function (event) {
        // Enter must never submit the form from these fields.
        if (event.key === 'Enter') {
          event.preventDefault();
          input.blur();
        }
      });
    });

    root.addEventListener('pointerdown', restorePhone);
  }

  window.FiruProductSelection = {
    isComplete: isComplete,
    firstProblem: firstProblem,
    focusProblem: focusProblem,
    getSubmitButton: function () { return getSubmitButton(getRoot()); },
    getForm: function () { return getForm(getRoot()); },
    hasBundleWidget: function () { return !!getRapiContent(); },
    getSelectedBar: function () { return getSelectedBar(getRapiContent()); },
    isInFlight: function () { return state.inFlight; },
    markStickyDispatch: function () { state.stickyDispatch = true; },
    setFieldError: setFieldError,
    resetFields: function () { resetFields(getRoot()); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
